import React, { useEffect, useRef, useState } from "react";
import { AccountLayout } from "./AccountLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import TwoFactorSettings from "./TwoFactorSettings";

export default function Settings() {
  const { user, setUser } = useAuth();
  const [prefs, setPrefs] = useState({ email_on_order_confirmed: true, email_on_crypto_sent: true });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  // Debounced PATCH: coalesces rapid toggles (e.g. accidental double-click) into
  // a single network call after 350ms of quiet. Optimistic UI in the meantime.
  const debounceRef = useRef(null);
  const pendingRef = useRef({});

  useEffect(() => {
    if (user?.notifications) setPrefs(user.notifications);
  }, [user]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const togglePref = (k, v) => {
    // 1. Optimistic UI update
    setPrefs((p) => ({ ...p, [k]: v }));
    // 2. Accumulate the change
    pendingRef.current = { ...pendingRef.current, [k]: v };
    // 3. Reset the debounce window
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const patch = pendingRef.current;
      pendingRef.current = {};
      try {
        const r = await api.patch("/auth/notifications", patch);
        setUser(r.data);
        toast.success("Preference saved");
      } catch (e) {
        // Roll back to the server-side truth on failure
        if (user?.notifications) setPrefs(user.notifications);
        toast.error("Update failed");
      }
    }, 350);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) { toast.error("New passwords do not match"); return; }
    if (pwd.next.length < 6) { toast.error("Password must be at least 6 chars"); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password changed");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Change failed"); }
    finally { setSavingPwd(false); }
  };

  return (
    <AccountLayout title="Settings" subtitle="Account, password, notifications">
      <div className="grid md:grid-cols-2 gap-5">
        <section className="xch-card p-5 space-y-3" data-testid="account-section">
          <SectionTitle title="Account" />
          <Field label="Email"><Input value={user?.email || ""} disabled className="rounded-xl bg-muted" data-testid="settings-email-input" /></Field>
          <Field label="Name"><Input value={user?.name || ""} disabled className="rounded-xl bg-muted" /></Field>
          <Field label="Country"><Input value={user?.country || ""} disabled className="rounded-xl bg-muted font-mono uppercase" /></Field>
        </section>

        <section className="xch-card p-5 space-y-3" data-testid="password-section">
          <SectionTitle title="Change password" subtitle="Pick something only you know — at least 6 characters." />
          <form onSubmit={changePassword} className="space-y-3">
            <Field label="Current password">
              <Input type="password" required value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                     autoComplete="current-password" className="rounded-xl" data-testid="settings-current-pwd" />
            </Field>
            <Field label="New password">
              <Input type="password" required minLength={6} value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                     autoComplete="new-password" className="rounded-xl" data-testid="settings-new-pwd" />
            </Field>
            <Field label="Confirm new password">
              <Input type="password" required minLength={6} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                     autoComplete="new-password" className="rounded-xl" data-testid="settings-confirm-pwd" />
            </Field>
            <Button type="submit" disabled={savingPwd}
                    className="bg-primary hover:bg-primary/90 text-white rounded-xl font-display font-semibold"
                    data-testid="settings-pwd-save-btn">
              {savingPwd ? "Saving…" : "Update password"}
            </Button>
          </form>
        </section>

        <section className="xch-card p-5 space-y-3 md:col-span-2" data-testid="notifications-section">
          <SectionTitle title="Notifications" />
          <div className="space-y-2">
            <Toggle label="Email me when an order is confirmed"
                    desc="We'll send a receipt as soon as your payment lands."
                    checked={prefs.email_on_order_confirmed}
                    onChange={(v) => togglePref("email_on_order_confirmed", v)}
                    testid="notif-order-confirmed" />
            <Toggle label="Email me when crypto is sent"
                    desc="Includes the on-chain transaction hash."
                    checked={prefs.email_on_crypto_sent}
                    onChange={(v) => togglePref("email_on_crypto_sent", v)}
                    testid="notif-crypto-sent" />
          </div>
        </section>

        <TwoFactorSettings />

        <section className="xch-card p-5 md:col-span-2 border-destructive/20" data-testid="danger-section">
          <SectionTitle title="Danger zone" />
          <p className="text-sm text-muted-foreground mb-3">
            We don't auto-delete accounts due to AML retention requirements. Email <strong className="text-foreground">support@xchangely.io</strong> to request a deletion.
          </p>
          <Button variant="outline"
                  onClick={() => toast.info("Email support@xchangely.io to request deletion.")}
                  className="rounded-xl border-destructive/40 text-destructive hover:bg-destructive/5"
                  data-testid="delete-account-btn">
            Delete my account
          </Button>
        </section>
      </div>
    </AccountLayout>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
    {children}
  </div>
);
const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-1">
    <div className="font-display text-base font-bold text-foreground">{title}</div>
    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
  </div>
);
const Toggle = ({ label, desc, checked, onChange, testid }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-border last:border-0" data-testid={testid}>
    <div>
      <div className="text-sm font-semibold text-foreground">{label}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={`${testid}-switch`} />
  </div>
);
